import { useTranslation } from 'react-i18next'

import { CountryDropdown } from '@/components/ui/CountryDropdown'
import { DatePicker, Field, Select } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import { useClients, useTransportRefs } from '@/features/directories/api/useDirectory'
import { ClientSelect } from '@/features/directories/components/ClientSelect'
import { transportModeLabel } from '@/lib/icons'
import type { CaseSummary, PaymentMethod, PaymentTermsKind } from '@/types/domain'

/* "New case" — a case with no quote behind it — and "Edit case".
 *
 * Creating a case WITHOUT a quote is a primary action (FR-70), not a fallback:
 * repeat business runs through it, and so do all ~241 migrated cases, which
 * arrive quote-less by definition. A case FROM a quote is not made here: it is
 * made by converting the quote (S-1, owner decision O4), which carries its
 * terms, costs and leg, names the Operations owner and closes the quote. The
 * «From quote» variant this sheet used to have picked a quote, filled the
 * client and the lane from it, and then never sent the link — `toCasePayload`
 * had no quote field — so it opened an unlinked twin and left the quote Sent.
 *
 * Client and transport mode are DIRECTORY PICKS carrying ids, not typed text
 * (FR-39). That is the substitution the product exists to make — the source
 * workbook joined its sheets on a company name somebody retyped each time, and
 * the same client arrived as four spellings — and it is also what the server
 * accepts: POST /cases takes a `clientId` and a `transportModeId`, so a typed
 * name had nothing to send and the create could never leave the sheet.
 */

export interface CaseDraft {
  clientId: string
  origin: string
  destination: string
  transportModeId: string
  eta: string
  /* Whether the client's final packing list is in hand (FR-12). The sheet has
     no control for it and never sets it: this sheet OPENS cases, and a case
     opened by hand has no packing list yet, so false is the fact rather than a
     placeholder. It is on the draft all the same because the payload sends the
     whole record — an amend sheet built on this shape must prefill the case's
     own flag, and a field it has to fill is harder to miss than a field it
     would have had to remember to add. */
  finalPackingListReceived: boolean
  /* How this shipment gets paid for (FR-32). Three fields rather than one
     phrase, and both the kind and the method are REQUIRED: the server refuses a
     case without them, so a sheet that did not collect them could never create
     one — an unpaid shipment with no recorded terms is the argument this product
     exists to prevent.

     '' is the unchosen state and is deliberately representable. The field is
     mandatory and there is no default: an empty required select is honest,
     where a pre-picked kind would put dated expectations in front of a client on
     the strength of nobody having looked at the field. */
  paymentTermsKind: PaymentTermsKind | ''
  paymentMethod: PaymentMethod | ''
  /* Days, as TEXT while it is being typed — a half-typed "4" is not the number
     4. Meaningful only for `credit`, and cleared when the kind moves away from
     it, because the server refuses a day count on any other kind. */
  paymentTermsCreditDays: string
}

export function CaseFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
}: {
  open: boolean
  onClose: () => void
  record?: CaseSummary
  onSubmit: (values: CaseDraft) => void
  pending?: boolean
}) {
  const { t } = useTranslation()
  // Searchable directory reads, so hundreds of entries stay usable. Each list is
  // the one this caller may see — the server decides that, not this sheet (AD-5).
  const { data: clients } = useClients()
  // `activeOnly`: a retired mode is not offered on a new case, though existing
  // cases keep the one they were opened with (FR-43).
  const { data: modes } = useTransportRefs('mode', true)

  /* An EDIT seeds its two pickers by resolving the summary row's labels against
     the directories, so it cannot seed correctly until those have arrived. The
     reset key carries that readiness: the sheet seeds blank if it opens first
     and re-seeds once, with the real ids, when the lists land. Without it a
     sheet opened on a cold cache would quietly drop the client it was editing.
     A create has nothing to resolve and is unaffected either way. */
  const resolved = Boolean(clients && modes)
  const [draft, setDraft, touched, markTouched] = useFormDraft<CaseDraft>(
    open ? `${record?.id ?? 'new'}:${record && !resolved ? 'pending' : 'ready'}` : null,
    () =>
      record
        ? {
            // A summary row carries the client's NAME for display, not its id,
            // so an edit resolves the id back through the directory rather than
            // sending the label the row happened to render.
            clientId:
              clients?.find((client) => client.name === record.clientName)?.id ?? '',
            origin: record.origin,
            destination: record.destination,
            /* The id the case carries, taken as given (QW-8). It was looked up
               by the DISPLAY mode — lowercased, and folded to nothing for a
               code this build does not know — which never matched the
               uppercase catalogue, so an amend would have cleared the mode. */
            transportModeId: record.transportModeId ?? '',
            eta: record.eta ?? '',
            /* Carried through untouched. The sheet shows no control for it, but
               the payload submits the whole record, so an amend that seeded
               false here would erase the flag conversion set the moment anybody
               corrected a typo in the destination. */
            finalPackingListReceived: record.finalPackingListReceived ?? false,
            paymentTermsKind: record.paymentTermsKind ?? '',
            paymentMethod: record.paymentMethod ?? '',
            paymentTermsCreditDays:
              record.paymentTermsCreditDays === undefined
                ? ''
                : String(record.paymentTermsCreditDays),
          }
        : emptyCase,
  )

  const clientError = touched && !draft.clientId ? t('common.form.required') : undefined
  const originError =
    touched && !draft.origin.trim() ? t('common.form.required') : undefined
  const destinationError =
    touched && !draft.destination.trim() ? t('common.form.required') : undefined
  const termsKindError =
    touched && !draft.paymentTermsKind ? t('common.form.required') : undefined
  const methodError =
    touched && !draft.paymentMethod ? t('common.form.required') : undefined
  /* Credit days are required exactly when the kind is `credit`, and refused on
     every other kind — the same rule in both directions the server enforces, so
     a case cannot arrive with a due date nobody agreed. */
  const creditDays = Number(draft.paymentTermsCreditDays)
  const creditDaysMissing =
    draft.paymentTermsKind === 'credit' &&
    (!draft.paymentTermsCreditDays.trim() ||
      !Number.isInteger(creditDays) ||
      creditDays < 1 ||
      creditDays > 365)
  const creditDaysError =
    touched && creditDaysMissing ? t('cases.form.creditDaysInvalid') : undefined
  const invalid =
    !draft.clientId ||
    !draft.origin.trim() ||
    !draft.destination.trim() ||
    !draft.paymentTermsKind ||
    !draft.paymentMethod ||
    creditDaysMissing

  const title = record ? t('cases.actions.editCase') : t('cases.actions.newCase')

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={title}
      submitLabel={record ? undefined : t('cases.actions.create')}
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >

      <Field label={t('cases.columns.client')} required error={clientError}>
        {(props) => (
          /* The quote form's picker: active clients searchable by name,
             registry number and code, and a create over this sheet for a
             caller who may register one (QW-12) — a case for a company the
             directory does not hold yet is no longer a dead end. */
          <ClientSelect
            value={draft.clientId}
            /* The name for a client this caller cannot list: the case being
               edited (a client reassigned since still names who it is for). */
            name={record?.clientName}
            /* An updater, not a spread of this render's draft: a client created
               from the picker is selected when the create answers, renders
               later, and a spread captured at Save would undo whatever was
               entered in between. */
            onChange={(clientId) => setDraft((current) => ({ ...current, clientId }))}
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        {/* Countries, the same ISO 3166-1 pickers the quote's route options use
            — a case opened from a quote inherits that quote's endpoints, so the
            two surfaces must speak in the same units or the carry across would
            put a code in a field that expects a port and back again.

            What it costs, and it is the same cost RouteOptionsCard records: a
            case opened through this sheet names a COUNTRY where the ~241
            migrated cases name a city. Both render — every reading surface goes
            through `placeName`, which passes an unrecognised value through as
            written — and neither is rewritten. */}
        <Field
          label={t('quotes.form.origin')}
          required
          error={originError}
          className="flex-1"
        >
          {(props) => (
            <CountryDropdown
              value={draft.origin}
              onChange={(origin) => setDraft({ ...draft, origin })}
              {...props}
            />
          )}
        </Field>

        <Field
          label={t('quotes.form.destination')}
          required
          error={destinationError}
          className="flex-1"
        >
          {(props) => (
            <CountryDropdown
              value={draft.destination}
              onChange={(destination) => setDraft({ ...draft, destination })}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* Not required: the server takes a case with no mode set yet, and the
          desk often opens one before the routing is decided. */}
      <Field label={t('quotes.form.mode')}>
        {(props) => (
          <Select
            value={draft.transportModeId}
            onChange={(transportModeId) => setDraft({ ...draft, transportModeId })}
            options={(modes ?? []).map((mode) => ({
              value: mode.id,
              // The catalogue's word for a code it knows, and the directory's
              // own label for one the desk added (FR-43) that no key covers.
              label: transportModeLabel(mode, t),
              hint: mode.code,
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>

      {/* ETA only, and no ETD beside it.
          A case header carries the day it opened and the day the cargo is
          expected; the DEPARTURE belongs to a leg (FR-24), and there is no
          header field to put one in. An ETD picker here collected a date the
          save then dropped — a field that appears to record something and does
          not is worse than an absent one. It returns with the legs. */}
      <Field label={t('cases.columns.eta')}>
        {(props) => (
          <DatePicker
            value={draft.eta}
            onChange={(eta) => setDraft({ ...draft, eta })}
            {...props}
          />
        )}
      </Field>

      {/* Required, unlike the same field on a quote: a quote is an offer that
          may still be negotiated, but a case is the shipment being run, and
          FR-32 makes the terms it runs under a matter of record.

          A pick from four rather than free text, because the terms have to
          become DATED EXPECTATIONS the platform can chase (FR-33) and no parse
          of a phrase somebody typed yields two dated amounts you would put in
          front of a client. */}
      <Field
        label={t('cases.form.paymentTermsKind')}
        required
        error={termsKindError}
      >
        {(props) => (
          <Select
            value={draft.paymentTermsKind}
            onChange={(kind) =>
              setDraft({
                ...draft,
                paymentTermsKind: kind as PaymentTermsKind,
                /* Cleared when the kind moves off `credit`: the server refuses
                   a day count on any other kind, and a number left behind here
                   would be read later as a due date. */
                paymentTermsCreditDays:
                  kind === 'credit' ? draft.paymentTermsCreditDays : '',
              })
            }
            options={termsKinds.map((kind) => ({
              value: kind,
              label: t(`cases.terms.${kind}`),
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>

      {/* Only for `credit`, where the days ARE the agreement — "net 30" and
          "net 45" are the same arrangement at two numbers. A numeric keyboard
          on the phone (UX-DR-27): this field takes digits and nothing else. */}
      {draft.paymentTermsKind === 'credit' ? (
        <Field
          label={t('cases.form.creditDays')}
          required
          error={creditDaysError}
          hint={t('cases.form.creditDaysHint')}
        >
          {(props) => (
            <Input
              value={draft.paymentTermsCreditDays}
              inputMode="numeric"
              onChange={(e) =>
                setDraft({ ...draft, paymentTermsCreditDays: e.target.value })
              }
              {...props}
            />
          )}
        </Field>
      ) : null}

      {/* How the money actually arrives. Operational rather than accounting:
          cash is collected by a person and a transfer is chased through a bank,
          which is different people and different follow-up. */}
      <Field label={t('cases.form.paymentMethod')} required error={methodError}>
        {(props) => (
          <Select
            value={draft.paymentMethod}
            onChange={(method) =>
              setDraft({ ...draft, paymentMethod: method as PaymentMethod })
            }
            options={paymentMethods.map((method) => ({
              value: method,
              label: t(`cases.method.${method}`),
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>

      <p className="text-text-muted text-[12px] leading-snug font-light">
        {t('cases.form.nextStep')}
      </p>
    </FormSheet>
  )
}

/* The five kinds and the two methods, in the order the desk reads them — the
   kinds in the source workbook's own order, prepayment first. Listed here
   rather than derived from the catalogue so that a kind added to one without
   the other fails the compile rather than rendering a blank option. */
const termsKinds: PaymentTermsKind[] = [
  'in_advance',
  'fifty_fifty',
  'full_on_delivery',
  'credit',
  'other',
]
const paymentMethods: PaymentMethod[] = ['bank_transfer', 'cash']

const emptyCase: CaseDraft = {
  clientId: '',
  origin: '',
  destination: '',
  transportModeId: '',
  eta: '',
  finalPackingListReceived: false,
  /* No default kind and no default method. The field is mandatory and an empty
     required select is honest — a default would be terms nobody chose. */
  paymentTermsKind: '',
  paymentMethod: '',
  paymentTermsCreditDays: '',
}
